import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await API.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [params, refresh]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="payment-success-page">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-10"><Logo /></div>
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
            <h1 className="mt-6 font-serif text-2xl font-bold" data-testid="payment-checking">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-success mx-auto" />
            <h1 className="mt-6 font-serif text-3xl font-bold" data-testid="payment-confirmed">Payment confirmed</h1>
            <p className="mt-2 text-sm text-muted-foreground">Your PAMind access is active. Let's set up your revision.</p>
            <Button data-testid="payment-continue-btn" onClick={() => navigate("/onboarding")} className="mt-8 px-8">
              Continue
            </Button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <h1 className="font-serif text-2xl font-bold" data-testid="payment-pending">Payment processing</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your payment is being processed. If your dashboard doesn't unlock within a few minutes, contact support.
            </p>
            <Button data-testid="payment-dashboard-btn" onClick={() => navigate("/app")} className="mt-8 px-8">Go to dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}
